"use client";

// Champs « entreprise » d'une facture, partagés par le tunnel de paiement, Mon
// compte et l'achat à vie d'une formation.
//
// Entreprise française : une recherche dans l'annuaire officiel
// (lib/companyLookup.js), par nom ou SIREN, remplit tout d'un clic, puis une
// carte résume l'entreprise choisie. La saisie à la main reste à un clic, et
// c'est la seule voie pour une entreprise étrangère. Le pays décide du reste :
// SIREN en France, numéro de TVA dans l'Union européenne.
//
// tone : « light » pour le tunnel de paiement, toujours clair (palette stone,
// sans variantes sombres qui jureraient) ; « auto » ailleurs (mode sombre).

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import useCountryOptions from "@/hooks/useCountryOptions";
import { getPostalCodeField } from "@/lib/billingAddress";
import {
    frenchVatFromSiren,
    isValidSiren,
    normalizeSiren,
    normalizeVat,
    usesSiren,
    vatPrefixFor,
} from "@/lib/companyBilling";
import { searchCompanies } from "@/lib/companyLookup";

export const EMPTY_COMPANY = {
    name: "",
    line1: "",
    line2: "",
    city: "",
    siren: "",
    vatNumber: "",
};

const TONES = {
    light: {
        muted: "text-stone-500",
        tag: "text-stone-400",
        link: "text-stone-500 underline decoration-stone-300 underline-offset-4 transition-colors hover:text-stone-800",
        card: "border-stone-200 bg-stone-50",
        strong: "text-stone-900",
        menu: "border-stone-200 bg-white shadow-lg",
        option: "text-stone-900",
        optionActive: "bg-stone-100",
    },
    // Onboarding : écran clair lui aussi, mais en gris (pas en stone)
    gray: {
        muted: "text-gray-500",
        tag: "text-gray-400",
        link: "text-gray-500 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-800",
        card: "border-gray-200 bg-gray-50",
        strong: "text-gray-900",
        menu: "border-gray-200 bg-white shadow-lg",
        option: "text-gray-900",
        optionActive: "bg-gray-100",
    },
    auto: {
        muted: "text-gray-500 dark:text-grayDark-400",
        tag: "text-gray-400 dark:text-grayDark-500",
        link: "text-gray-500 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-900 dark:text-grayDark-400 dark:decoration-white/20 dark:hover:text-white",
        card: "border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5",
        strong: "text-gray-900 dark:text-white",
        menu: "border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-bgDark-700",
        option: "text-gray-900 dark:text-white",
        optionActive: "bg-gray-100 dark:bg-white/10",
    },
};

const formatSiren = (siren) =>
    siren.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3");
const formatFrenchVat = (vat) =>
    vat.replace(/^(FR\w{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4");

export default function CompanyBillingFields({
    idPrefix,
    value,
    onChange,
    labelClassName,
    inputClassName,
    selectClassName,
    tone = "auto",
}) {
    const t = useTranslations();
    const countryOptions = useCountryOptions();
    const styles = TONES[tone] || TONES.auto;

    const country = value.country || "";
    const postalField = getPostalCodeField(country);
    const vatPrefix = vatPrefixFor(country);
    const sirenCountry = usesSiren(country);
    const siren = normalizeSiren(value.siren);

    // Annuaire (entreprise française) ou saisie à la main
    const [manual, setManual] = useState(false);
    const [showLine2, setShowLine2] = useState(false);
    const searching = sirenCountry && !manual;
    const chosen = searching && Boolean(value.name && siren);

    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | loading | done | error
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const listId = `${idPrefix}-results`;

    useEffect(() => {
        if (!searching || chosen) return undefined;
        const q = query.trim();
        if (q.length < 3) {
            setResults([]);
            setStatus("idle");
            return undefined;
        }
        const controller = new AbortController();
        setStatus("loading");
        // Une recherche par frappe ralentie : l'annuaire limite les appels
        const timer = setTimeout(() => {
            searchCompanies(q, { signal: controller.signal })
                .then((list) => {
                    setResults(list);
                    setActive(list.length ? 0 : -1);
                    setStatus("done");
                })
                .catch((error) => {
                    if (error?.name === "AbortError") return;
                    setResults([]);
                    setStatus("error");
                });
        }, 250);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query, searching, chosen]);

    // Verdict du registre européen sur le numéro de TVA, à la demande. Tout
    // changement du numéro le périme.
    const [vatCheck, setVatCheck] = useState({ status: "idle" });
    useEffect(() => {
        setVatCheck({ status: "idle" });
    }, [value.vatNumber, country]);

    const checkVat = async () => {
        setVatCheck({ status: "checking" });
        try {
            const vat = normalizeVat(value.vatNumber, country);
            const response = await fetch(
                `/api/company/vat-lookup?country=${country}&vat=${encodeURIComponent(vat)}`,
            );
            const data = await response.json();
            if (!response.ok) {
                setVatCheck({ status: "unknown" });
                return;
            }
            setVatCheck({ status: data.status, name: data.name });
            // Raison sociale déclarée au registre : elle remplit le formulaire
            // d'une entreprise étrangère, qui n'a pas notre annuaire.
            if (data.status === "valid" && data.name && !value.name) {
                onChange({ name: data.name });
            }
        } catch (error) {
            setVatCheck({ status: "unavailable" });
        }
    };

    const pick = (company) => {
        onChange({ ...company, vatNumber: value.vatNumber || "" });
        setQuery("");
        setResults([]);
        setStatus("idle");
        setOpen(false);
    };

    const searchAgain = () => {
        onChange({ ...EMPTY_COMPANY, postalCode: "" });
        setManual(false);
    };

    const onSearchKeyDown = (event) => {
        // Entrée ne doit jamais envoyer le paiement depuis ce champ
        if (event.key === "Enter") {
            event.preventDefault();
            if (open && active >= 0 && results[active]) pick(results[active]);
            return;
        }
        if (!results.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActive((index) => (index + 1) % results.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            setActive((index) => (index - 1 + results.length) % results.length);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    };

    const label = (name, text, optional = false) => (
        <label
            htmlFor={`${idPrefix}-${name}`}
            className={`${labelClassName} flex items-baseline justify-between gap-2`}
        >
            <span>{text}</span>
            {optional && (
                <span className={`text-[12px] font-normal ${styles.tag}`}>
                    {t("billing_company_optional")}
                </span>
            )}
        </label>
    );

    const field = (name, text, props = {}, optional = false) => (
        <div className="group">
            {label(name, text, optional)}
            <input
                id={`${idPrefix}-${name}`}
                type="text"
                className={inputClassName}
                value={value[name] || ""}
                onChange={(event) => onChange({ [name]: event.target.value })}
                {...props}
            />
        </div>
    );

    const postalCodeInput = postalField.visible &&
        field(
            "postalCode",
            postalField.required
                ? t(postalField.labelKey)
                : t("address_zipCode"),
            {
                autoComplete: "postal-code",
                placeholder: postalField.example,
                inputMode: postalField.inputMode,
                autoCapitalize: postalField.autoCapitalize,
                spellCheck: false,
            },
            !postalField.required,
        );

    const suggestedVat =
        vatPrefix === "FR" && isValidSiren(siren) ? frenchVatFromSiren(siren) : null;

    return (
        <div className="space-y-4">
            <div className="group">
                {label("country", t("billing_company_country"))}
                <div className="relative">
                    <select
                        id={`${idPrefix}-country`}
                        className={`${selectClassName || inputClassName} appearance-none !pr-9`}
                        autoComplete="country"
                        value={country}
                        onChange={(event) => onChange({ country: event.target.value })}
                    >
                        {countryOptions.map((option) => (
                            <option key={option.code} value={option.code}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <SolarIcon
                        name="alt-arrow-down-linear"
                        className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${styles.tag}`}
                    />
                </div>
            </div>

            {searching && !chosen && (
                <div className="group relative">
                    {label("search", t("billing_company_search_label"))}
                    <div className="relative">
                        {/* « ! » obligatoire : la classe globale .input
                            applique px-3 et l'emporte sur pl-9, si bien que
                            la place réservée à la loupe était reprise et le
                            texte se posait dessous. */}
                        <SolarIcon
                            name="magnifer-linear"
                            className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${styles.tag}`}
                        />
                        <input
                            id={`${idPrefix}-search`}
                            type="text"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={open && results.length > 0}
                            aria-controls={listId}
                            aria-activedescendant={
                                open && active >= 0 ? `${listId}-${active}` : undefined
                            }
                            autoComplete="off"
                            spellCheck={false}
                            className={`${inputClassName} !pl-9`}
                            placeholder={t("billing_company_search_placeholder")}
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setOpen(true);
                            }}
                            onFocus={() => setOpen(true)}
                            onBlur={() => setOpen(false)}
                            onKeyDown={onSearchKeyDown}
                        />
                    </div>
                    {open && results.length > 0 && (
                        <ul
                            id={listId}
                            role="listbox"
                            className={`absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-[10px] border py-1 ${styles.menu}`}
                        >
                            {results.map((company, index) => (
                                <li
                                    key={company.siren}
                                    id={`${listId}-${index}`}
                                    role="option"
                                    aria-selected={index === active}
                                    // mousedown : avant que le champ ne perde le
                                    // focus et ne ferme la liste
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        pick(company);
                                    }}
                                    onMouseEnter={() => setActive(index)}
                                    className={`cursor-pointer px-3 py-2 ${styles.option} ${
                                        index === active ? styles.optionActive : ""
                                    }`}
                                >
                                    <span className="block text-[14px] font-medium leading-5">
                                        {company.name}
                                    </span>
                                    <span className={`block text-[12px] leading-4 ${styles.muted}`}>
                                        {t("billing_company_search_result", {
                                            siren: formatSiren(company.siren),
                                            city: `${company.postalCode} ${company.city}`.trim(),
                                        })}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className={`mb-0 mt-2 text-[12px] leading-relaxed ${styles.muted}`}>
                        {status === "loading" && (
                            <span className="mr-1">{t("billing_company_search_loading")}</span>
                        )}
                        {status === "error" && (
                            <span className="mr-1">{t("billing_company_search_error")}</span>
                        )}
                        {status === "done" && results.length === 0 && (
                            <span className="mr-1">{t("billing_company_search_empty")}</span>
                        )}
                        <button
                            type="button"
                            className={`font-medium ${styles.link}`}
                            onClick={() => setManual(true)}
                        >
                            {t("billing_company_manual")}
                        </button>
                    </p>
                </div>
            )}

            {chosen && (
                <div className={`flex items-start justify-between gap-3 rounded-[10px] border p-3 ${styles.card}`}>
                    <div className="flex min-w-0 items-start gap-3">
                        <SolarIcon
                            name="buildings-2-linear"
                            className={`mt-0.5 h-5 w-5 shrink-0 ${styles.tag}`}
                        />
                        <div className="min-w-0">
                            <p className={`mb-0 text-[14px] font-semibold leading-5 ${styles.strong}`}>
                                {value.name}
                            </p>
                            <p className={`mb-0 mt-0.5 text-[12px] leading-4 ${styles.muted}`}>
                                {[value.line1, value.line2].filter(Boolean).join(", ")}
                                {" · "}
                                {`${value.postalCode || ""} ${value.city || ""}`.trim()}
                            </p>
                            <p className={`mb-0 mt-0.5 text-[12px] leading-4 ${styles.muted}`}>
                                SIREN {formatSiren(siren)}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={`shrink-0 text-[12px] font-medium ${styles.link}`}
                        onClick={() => setManual(true)}
                    >
                        {t("billing_company_change")}
                    </button>
                </div>
            )}

            {!searching && (
                <>
                    {field("name", t("billing_company_name"), {
                        autoComplete: "organization",
                    })}
                    {field("line1", t("billing_company_street"), {
                        autoComplete: "address-line1",
                    })}
                    {showLine2 || value.line2 ? (
                        field(
                            "line2",
                            t("billing_company_street_option"),
                            { autoComplete: "address-line2" },
                            true,
                        )
                    ) : (
                        <button
                            type="button"
                            className={`-mt-2 flex items-center gap-1 text-[12px] font-medium ${styles.link}`}
                            onClick={() => setShowLine2(true)}
                        >
                            <SolarIcon name="add-circle-linear" className="h-4 w-4" />
                            {t("billing_company_add_line2")}
                        </button>
                    )}
                    <div
                        className={`grid grid-cols-1 gap-4 ${
                            postalField.visible ? "sm:grid-cols-2" : ""
                        }`}
                    >
                        {postalCodeInput}
                        {field("city", t("address_city"), {
                            autoComplete: "address-level2",
                        })}
                    </div>
                    {sirenCountry &&
                        field("siren", t("billing_company_siren"), {
                            inputMode: "numeric",
                            placeholder: "123 456 789",
                            spellCheck: false,
                        })}
                    {sirenCountry && (
                        <button
                            type="button"
                            className={`-mt-2 text-[12px] font-medium ${styles.link}`}
                            onClick={searchAgain}
                        >
                            {t("billing_company_search_again")}
                        </button>
                    )}
                </>
            )}

            {/* Service comptable : la facture y part à chaque paiement */}
            <div className="group">
                {label("email", t("billing_company_email"), true)}
                <input
                    id={`${idPrefix}-email`}
                    type="email"
                    className={inputClassName}
                    value={value.email || ""}
                    onChange={(event) => onChange({ email: event.target.value })}
                    placeholder="compta@entreprise.fr"
                    autoComplete="email"
                    spellCheck={false}
                />
                <p className={`mb-0 mt-2 text-[12px] leading-relaxed ${styles.muted}`}>
                    {t("billing_company_email_help")}
                </p>
            </div>

            {vatPrefix && (
                <div className="group">
                    {label("vatNumber", t("billing_company_vat"), true)}
                    <input
                        id={`${idPrefix}-vatNumber`}
                        type="text"
                        className={inputClassName}
                        value={value.vatNumber || ""}
                        onChange={(event) => onChange({ vatNumber: event.target.value })}
                        placeholder={`${vatPrefix}123456789`}
                        autoCapitalize="characters"
                        spellCheck={false}
                    />
                    {suggestedVat && !value.vatNumber && (
                        <button
                            type="button"
                            className={`mt-2 text-[12px] font-medium ${styles.link}`}
                            onClick={() => onChange({ vatNumber: suggestedVat })}
                        >
                            {t("billing_company_vat_suggest", {
                                vat: formatFrenchVat(suggestedVat),
                            })}
                        </button>
                    )}
                    {/* Le registre européen répond tout de suite, alors que
                        Stripe ne vérifie ce numéro qu'APRÈS le paiement : un
                        numéro inconnu se corrige donc avant de facturer sans
                        TVA. Et la raison sociale déclarée remplit le
                        formulaire d'une entreprise étrangère. */}
                    {value.vatNumber && (
                        <p className={`mb-0 mt-2 text-[12px] leading-relaxed ${styles.muted}`}>
                            {vatCheck.status === "checking" ? (
                                t("billing_company_vat_checking")
                            ) : vatCheck.status === "valid" ? (
                                <span className="font-medium text-green-600">
                                    {vatCheck.name
                                        ? t("billing_company_vat_valid_named", {
                                              name: vatCheck.name,
                                          })
                                        : t("billing_company_vat_valid")}
                                </span>
                            ) : vatCheck.status === "unknown" ? (
                                <span className="font-medium text-amber-600">
                                    {t("billing_company_vat_unknown")}
                                </span>
                            ) : vatCheck.status === "unavailable" ? (
                                t("billing_company_vat_unavailable")
                            ) : (
                                <button
                                    type="button"
                                    className={`font-medium ${styles.link}`}
                                    onClick={checkVat}
                                >
                                    {t("billing_company_vat_check")}
                                </button>
                            )}
                        </p>
                    )}
                    {/* Autoliquidation : seulement pour une entreprise d'un
                        autre pays de l'Union, avec son numéro de TVA */}
                    {vatPrefix !== "FR" && (
                        <p className={`mb-0 mt-2 text-[12px] leading-relaxed ${styles.muted}`}>
                            {t("billing_company_vat_reverse_charge")}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
